import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { insertIf } from '@backmarket/utils/collection/insertIf'
import { objectKeys } from '@backmarket/utils/object/objectKeys'

import type { UploadedFileValue } from '~/api/reviews/types/GetReviewCollectionResponse'
import type { PostReviewCollectionBody } from '~/api/reviews/types/PostReviewCollectionBody'

import type {
  FileValue,
  ReviewForm,
  ReviewFormQuestion,
} from '../models/review-form'

import { useReviewCollectionSource } from './useReviewCollectionSource'

const buildApiValue = (
  question: ReviewFormQuestion,
  value: string | number | UploadedFileValue[] | FileValue[],
) => {
  switch (question.type) {
    case 'rating_bar':
      return { rate: value }
    case 'textarea':
      return { comment: value }
    case 'file_upload':
      return {
        attachments: value,
      }
    default:
      return {}
  }
}

function buildBody(
  values: ReviewForm,
  questions: ReviewFormQuestion[],
  source: string,
): PostReviewCollectionBody {
  const answers: PostReviewCollectionBody['answers'] = []

  objectKeys(values).forEach((identifier) => {
    const question = questions.find((q) => q.identifier === identifier)

    // form values may still hold values from previous steps.
    // In this case, we won't find the associated question and we don't want to add them to the body
    if (!question) return

    const value = buildApiValue(question, values[identifier])

    answers.push({
      identifier,
      value,
    })
  })

  return {
    answers,
    source,
  }
}

export function usePostOrderReviewCollection() {
  const source = useReviewCollectionSource().source.value
  const logger = useLogger()
  const { openErrorToast } = useTheToast()
  const $httpFetch = useHttpFetch()

  async function submit({
    stepValues,
    questions,
    orderlineId,
    authToken,
  }: {
    stepValues: ReviewForm
    questions: ReviewFormQuestion[]
    orderlineId: string | string[]
    authToken?: string
  }): Promise<[number, undefined] | [undefined, Error]> {
    try {
      const responseData = await $httpFetch(
        '/reviews/v3/sales-order-item/:salesOrderItemUuid/review',
        {
          method: 'POST',
          pathParams: { salesOrderItemUuid: orderlineId.toString() },
          body: buildBody(stepValues, questions, source),
          query: insertIf(!!authToken, { authToken }),
        },
      )

      return [responseData.averageRate, undefined]
    } catch (error) {
      logger.error('[ReviewCollection] POST', {
        error: error as Error,
        owners: ['bot-squad-content-front'],
      })
      openErrorToast()

      return [undefined, error as Error]
    }
  }

  return submit
}
